/**
 * Cookie Consent Management Utility
 * Handles GDPR-compliant cookie consent for the application
 */

// Cookie categories
export const COOKIE_CATEGORIES = {
  STRICTLY_NECESSARY: 'strictly_necessary',
  FUNCTIONAL: 'functional',
  ANALYTICS: 'analytics',
  MARKETING: 'marketing'
};

// Cookie consent storage key (this is a strictly necessary cookie)
const CONSENT_STORAGE_KEY = 'cookie_consent';
const CONSENT_TIMESTAMP_KEY = 'cookie_consent_timestamp';

/**
 * Get current cookie consent preferences
 * @returns {Object} Consent preferences object
 */
export const getCookieConsent = () => {
  try {
    const consent = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!consent) return null;
    
    return JSON.parse(consent);
  } catch (error) {
    console.error('Error reading cookie consent:', error);
    return null;
  }
};

/**
 * Check if user has given consent
 * @returns {boolean} True if user has made a choice
 */
export const hasConsentChoice = () => {
  return getCookieConsent() !== null;
};

/**
 * Set cookie consent preferences
 * @param {Object} preferences - Consent preferences for each category
 */
export const setCookieConsent = (preferences) => {
  try {
    const consentData = {
      ...preferences,
      timestamp: new Date().toISOString(),
      version: '1.0' // For future consent updates
    };
    
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(consentData));
    localStorage.setItem(CONSENT_TIMESTAMP_KEY, consentData.timestamp);
    
    // Apply consent immediately
    applyConsent(preferences);
  } catch (error) {
    console.error('Error setting cookie consent:', error);
  }
};

/**
 * Check if a specific cookie category is allowed
 * @param {string} category - Cookie category from COOKIE_CATEGORIES
 * @returns {boolean} True if category is allowed
 */
export const isCategoryAllowed = (category) => {
  // Strictly necessary cookies are always allowed
  if (category === COOKIE_CATEGORIES.STRICTLY_NECESSARY) {
    return true;
  }
  
  const consent = getCookieConsent();
  if (!consent) return false;
  
  return consent[category] === true;
};

/**
 * Accept all cookies
 */
export const acceptAllCookies = () => {
  setCookieConsent({
    [COOKIE_CATEGORIES.STRICTLY_NECESSARY]: true,
    [COOKIE_CATEGORIES.FUNCTIONAL]: true,
    [COOKIE_CATEGORIES.ANALYTICS]: true,
    [COOKIE_CATEGORIES.MARKETING]: true
  });
};

/**
 * Reject all non-essential cookies
 */
export const rejectAllCookies = () => {
  setCookieConsent({
    [COOKIE_CATEGORIES.STRICTLY_NECESSARY]: true,
    [COOKIE_CATEGORIES.FUNCTIONAL]: false,
    [COOKIE_CATEGORIES.ANALYTICS]: false,
    [COOKIE_CATEGORIES.MARKETING]: false
  });
  
  // Clear non-essential data
  clearNonEssentialData();
};

/**
 * Clear non-essential data from storage
 */
const clearNonEssentialData = () => {
  try {
    // Remove language preference (functional cookie)
    localStorage.removeItem('appLanguage');
    
    // Add any other non-essential items here in the future
  } catch (error) {
    console.error('Error clearing non-essential data:', error);
  }
};

/**
 * Apply consent preferences (e.g., load/unload third-party scripts)
 * @param {Object} preferences - Consent preferences
 */
const applyConsent = (preferences) => {
  // If functional cookies are allowed, ensure language preference works
  if (!preferences[COOKIE_CATEGORIES.FUNCTIONAL]) {
    // Remove language preference if functional cookies are rejected
    localStorage.removeItem('appLanguage');
  }
  
  // Future: Load/unload analytics scripts based on analytics consent
  if (preferences[COOKIE_CATEGORIES.ANALYTICS]) {
    // Load analytics scripts
  } else {
    // Unload analytics scripts
  }
  
  // Future: Load/unload marketing scripts based on marketing consent
  if (preferences[COOKIE_CATEGORIES.MARKETING]) {
    // Load marketing scripts
  } else {
    // Unload marketing scripts
  }
};

/**
 * Safe localStorage setItem that checks consent first
 * @param {string} key - Storage key
 * @param {string} value - Storage value
 * @param {string} category - Cookie category (default: FUNCTIONAL)
 */
export const setItemWithConsent = (key, value, category = COOKIE_CATEGORIES.FUNCTIONAL) => {
  if (isCategoryAllowed(category)) {
    try {
      localStorage.setItem(key, value);
      return true;
    } catch (error) {
      console.error('Error setting item in localStorage:', error);
      return false;
    }
  }
  return false;
};

/**
 * Reset all consent preferences (for testing or user request)
 */
export const resetConsent = () => {
  localStorage.removeItem(CONSENT_STORAGE_KEY);
  localStorage.removeItem(CONSENT_TIMESTAMP_KEY);
};
